import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, Highlight, Icon, Tooltip } from '@node-space/ui-components'
import { DEFAULT_DOC_ICON, DOC_TYPE_ICON } from 'pages/EmbeddedCustomers/helpers/constants'
import { CustomerApplicationDetails } from 'types/customers'
import {
  ASSOCIATE_TYPE_TRANSLATION_KEYS,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID,
} from '../helpers/constants'

interface CustomerAssociatesTabProps {
  associates: CustomerApplicationDetails
}

export const CustomerAssociatesTab = ({ associates }: CustomerAssociatesTabProps) => {
  const { t } = useTranslation()

  const missingRoles =
    associates?.requiredActions
      ?.filter(
        ({ category, code }) =>
          category === REQUIRED_ACTION_CATEGORY_ASSOCIATES &&
          code !== REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID
      )
      .map(({ code }) => code) ?? []

  const existingAssociates = associates?.company?.associates ?? []

  return (
    <Box flexCol paddingY={16}>
      {missingRoles.map(code => (
        <DataListItem
          key={code}
          id={code}
          layout="vertical"
          title={t(ASSOCIATE_TYPE_TRANSLATION_KEYS[code] ?? code)}
          body=""
          testId="customer-associate-missing-role"
          HeaderHighlight={
            <Highlight type="warning">{t('embeddedCustomers.status.infoRequired')}</Highlight>
          }
        />
      ))}

      {existingAssociates.map(associate => {
        const isComplete = associate.validation?.status === 'COMPLETE'
        const allDocs = [
          ...associate.entity.identificationDocuments.map(d => d.type),
          ...associate.entity.proofOfAddresses.map(d => d.documentType),
        ]
        return (
          <Box key={associate.id} flexCol className="gap-1">
            <DataListItem
              id={associate.id}
              layout="vertical"
              title={associate.types
                .map(type => t(ASSOCIATE_TYPE_TRANSLATION_KEYS[type] ?? type))
                .join(' / ')}
              body={`${associate.entity.firstName} ${associate.entity.lastName}`}
              testId="customer-associate"
              HeaderHighlight={
                <Highlight type={isComplete ? 'success' : 'warning'}>
                  {isComplete
                    ? t('embeddedCustomers.status.verified')
                    : t('embeddedCustomers.status.infoRequired')}
                </Highlight>
              }
            />
            {!isComplete && !!allDocs.length && (
              <Box testId="associate-doc-icons" className="flex flex-row gap-3 pb-2 justify-end">
                {allDocs.map((docType, idx) => {
                  const mapping = DOC_TYPE_ICON[docType]
                  const iconName = mapping?.icon ?? DEFAULT_DOC_ICON
                  const label = mapping ? t(mapping.labelKey, { defaultValue: docType }) : docType
                  return (
                    <Tooltip key={`${docType}-${idx}`} bodyContent={label}>
                      <Box className="text-foreground-subdued hover:text-foreground-prominent transition-colors cursor-default">
                        <Icon name={iconName} size="medium" />
                      </Box>
                    </Tooltip>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
